import {
  ControllerComponents,
  ControllerProps,
} from "@/components/inspector/controller/Controllers";
import { TabNameType } from "@/components/inspector/tab/InspectorTabs";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { i as i_v1_1 } from "@henge-inc/ui/v1_1";
import { AnimatePresence, motion } from "motion/react";

type InspectorTabLayoutType = "flex" | "grid";

export interface InspectorTabProps {
  label: TabNameType;
  layout: InspectorTabLayoutType;
  items: ControllerProps[];
  open: boolean;
  onClick: () => void;
}

export const Tab = ({
  label,
  layout,
  items,
  open,
  onClick,
}: InspectorTabProps) => {
  return (
    <motion.div className="flex flex-col w-full">
      <div
        className="flex w-full p-1 justify-between items-center cursor-pointer h-8"
        onClick={onClick}
      >
        <HUI.Text
          kind={HENGE.TEXT_KIND.small}
          weight={HENGE.TEXT_WEIGHT.medium}
          colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
          className="select-none"
        >
          {label}
        </HUI.Text>
        <div
          style={{
            transform: `rotate(${open ? 0 : 180}deg)`,
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <i_v1_1.ChevronDown
            kind={HENGE.ICON_KIND.regular}
            size={HENGE.DETAILED_SIZE.small}
            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
          />
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: layout,
                flexDirection: "column",
                gridTemplateColumns: "repeat(3, 1fr)",
                gridColumnGap: layout === "grid" ? "8px" : "0px",
                gridRowGap: layout === "grid" ? "12px" : "0px",
                padding: layout === "grid" ? "8px 0px" : "0px",
              }}
            >
              {Object.entries(items).map(([key, props]) => {
                switch (layout) {
                  case "flex":
                    return (
                      <div
                        key={key}
                        className="flex p-1 justify-between items-center h-10"
                      >
                        <HUI.Text
                          kind={HENGE.TEXT_KIND.small}
                          weight={HENGE.TEXT_WEIGHT.medium}
                          colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                          className="w-full select-none"
                        >
                          {props.label}
                        </HUI.Text>
                        {ControllerComponents(props)}
                      </div>
                    );
                  case "grid":
                    return (
                      <div
                        key={key}
                        className="flex w-full flex-col justify-center items-center gap-2"
                      >
                        {ControllerComponents(props)}
                        <div className="w-full">
                          <HUI.Text
                            kind={HENGE.TEXT_KIND.small}
                            weight={HENGE.TEXT_WEIGHT.medium}
                            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                            align={HENGE.TEXT_ALIGN.center}
                            whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                            className="w-full select-none"
                            style={{
                              fontSize: "clamp(8px, 1.2vw, 12px)",
                              textOverflow: "clip",
                              overflow: "visible",
                            }}
                          >
                            {props.label}
                          </HUI.Text>
                        </div>
                      </div>
                    );
                }
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
